import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { HIRING_CONTACTS, type HiringContact } from '../data/hiringContacts';

/* ─── LinkedIn connected tracking (localStorage) ─────────────────────────── */

const CONNECTED_KEY = 'linkedin_connected';

function loadConnected(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CONNECTED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function saveConnected(set: Set<string>) {
  localStorage.setItem(CONNECTED_KEY, JSON.stringify([...set]));
}

/* ─── Keyword styling map ─────────────────────────────────────────────────── */

const KEYWORD_STYLES: Record<string, string> = {
  recruiter: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'talent acquisition': 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  'recruiting/recruitment': 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  'people/talent leadership': 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  'hrbp/business partner': 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
  sourcer: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
  headhunter: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  hiring: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  'seleccion/reclutamiento': 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
};

const GRADIENTS = [
  'from-rose-400 to-orange-300',
  'from-violet-400 to-fuchsia-300',
  'from-cyan-400 to-blue-400',
  'from-emerald-400 to-teal-300',
  'from-amber-400 to-yellow-300',
  'from-indigo-400 to-purple-300',
  'from-pink-400 to-rose-300',
  'from-sky-400 to-cyan-300',
  'from-lime-400 to-green-300',
  'from-fuchsia-400 to-pink-300',
];

function getGradient(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

function getInitials(first: string, last: string) {
  const f = first.charAt(0).toUpperCase();
  const l = last.charAt(0).toUpperCase();
  return f + l;
}

/* ─── Particles background (subtle network vibe) ──────────────────────────── */

function NetworkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const nodes: { x: number; y: number; vx: number; vy: number; r: number }[] = [];
    const NODE_COUNT = 40;
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 1,
      });
    }

    function draw() {
      ctx!.clearRect(0, 0, w, h);
      const isDark = document.documentElement.classList.contains('dark');
      ctx!.strokeStyle = isDark ? 'rgba(192,193,255,0.06)' : 'rgba(65,67,200,0.06)';
      ctx!.fillStyle = isDark ? 'rgba(192,193,255,0.12)' : 'rgba(65,67,200,0.12)';

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        a.x += a.vx;
        a.y += a.vy;
        if (a.x < 0 || a.x > w) a.vx *= -1;
        if (a.y < 0 || a.y > h) a.vy *= -1;

        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.lineWidth = 1 - dist / 120;
            ctx!.stroke();
          }
        }

        ctx!.beginPath();
        ctx!.arc(a.x, a.y, a.r, 0, Math.PI * 2);
        ctx!.fill();
      }
      animId = requestAnimationFrame(draw);
    }

    draw();

    const onResize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx!.scale(dpr, dpr);
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

/* ─── Category chip ───────────────────────────────────────────────────────── */

function CategoryChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
        active
          ? 'bg-primary text-on-primary border-primary shadow-sm'
          : 'bg-surface-container-high text-on-surface-variant border-outline-variant/20 hover:border-outline-variant/40 hover:bg-surface-container'
      }`}
    >
      <span className="truncate max-w-[140px]">{label}</span>
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
          active ? 'bg-on-primary/20 text-on-primary' : 'bg-surface-container-highest text-on-surface-variant'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/* ─── Contact card ────────────────────────────────────────────────────────── */

function ContactCard({
  contact,
  index,
  connected,
  onConnect,
}: {
  contact: HiringContact;
  index: number;
  connected: boolean;
  onConnect: (url: string) => void;
}) {
  const gradient = getGradient(contact.firstName + contact.lastName);
  const initials = getInitials(contact.firstName, contact.lastName);

  return (
    <div
      className={`group relative flex flex-col rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 ${
        connected
          ? 'border-green-500/30 bg-green-500/5'
          : 'border-outline-variant/10 bg-surface-container-low hover:border-outline-variant/25'
      }`}
      style={{ animationDelay: `${Math.min(index * 20, 600)}ms` }}
    >
      {connected && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 border border-green-500/25">
          <span className="material-symbols-outlined text-green-500 text-[12px]">check_circle</span>
          <span className="text-[10px] font-bold text-green-600 dark:text-green-400">Visited</span>
        </div>
      )}

      {/* Top row */}
      <div className="flex items-start gap-4">
        <div
          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold text-sm shadow-md shrink-0`}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-on-surface truncate leading-tight">
            {contact.firstName} {contact.lastName}
          </h3>
          <p className="text-xs text-on-surface-variant mt-0.5 truncate">{contact.position}</p>
          {contact.company && (
            <p className="text-[11px] text-on-surface-variant/70 mt-0.5 truncate flex items-center gap-1">
              <span className="material-symbols-outlined text-[12px]">apartment</span>
              {contact.company}
            </p>
          )}
        </div>
      </div>

      {/* Keywords */}
      <div className="flex flex-wrap gap-1.5 mt-4">
        {contact.keywords.map((kw) => (
          <span
            key={kw}
            className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
              KEYWORD_STYLES[kw] ?? 'bg-surface-container-high text-on-surface-variant border-outline-variant/20'
            }`}
          >
            {kw}
          </span>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-auto pt-4">
        <a
          href={contact.url}
          target="_blank"
          rel="noreferrer"
          onClick={() => onConnect(contact.url)}
          className={`flex items-center justify-center gap-2 w-full py-2 rounded-xl text-xs font-bold transition-all duration-200 active:scale-[0.98] ${
            connected
              ? 'bg-green-500/15 border border-green-500/30 text-green-600 dark:text-green-400 hover:bg-green-500/25'
              : 'primary-gradient text-on-primary hover:opacity-90 hover:shadow-lg hover:shadow-primary/20'
          }`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          {connected ? 'Profile visited' : 'Connect on LinkedIn'}
        </a>
      </div>
    </div>
  );
}

/* ─── Empty state ──────────────────────────────────────────────────────────── */

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
      <div className="w-20 h-20 rounded-full bg-surface-container-high flex items-center justify-center mb-4">
        <span className="material-symbols-outlined text-4xl text-on-surface-variant">search_off</span>
      </div>
      <h3 className="text-lg font-bold text-on-surface mb-1">No contacts found</h3>
      <p className="text-sm text-on-surface-variant max-w-xs mb-4">
        Try adjusting your search or filters to discover more connections.
      </p>
      <button
        onClick={onClear}
        className="px-4 py-2 rounded-lg bg-surface-container-high text-on-surface text-xs font-bold hover:bg-surface-container transition-colors"
      >
        Clear filters
      </button>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────── */

export default function Contacts() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [visibleCount, setVisibleCount] = useState(24);
  const [luckyContact, setLuckyContact] = useState<HiringContact | null>(null);
  const [connected, setConnected] = useState<Set<string>>(() => loadConnected());

  const handleConnect = useCallback((url: string) => {
    setConnected((prev) => {
      const next = new Set(prev);
      next.add(url);
      saveConnected(next);
      return next;
    });
  }, []);

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    map.set('all', HIRING_CONTACTS.length);
    for (const c of HIRING_CONTACTS) {
      for (const kw of c.keywords) {
        map.set(kw, (map.get(kw) || 0) + 1);
      }
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return HIRING_CONTACTS.filter((c) => {
      const matchesSearch =
        !q ||
        c.firstName.toLowerCase().includes(q) ||
        c.lastName.toLowerCase().includes(q) ||
        c.company.toLowerCase().includes(q) ||
        c.position.toLowerCase().includes(q);
      const matchesCategory = activeCategory === 'all' || c.keywords.includes(activeCategory);
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + 24);
  }, []);

  const handleClear = useCallback(() => {
    setSearch('');
    setActiveCategory('all');
    setVisibleCount(24);
  }, []);

  const handleLucky = useCallback(() => {
    const pool = activeCategory === 'all' ? HIRING_CONTACTS : filtered;
    if (pool.length === 0) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setLuckyContact(pick);
    setTimeout(() => setLuckyContact(null), 4000);
  }, [activeCategory, filtered]);

  useEffect(() => {
    setVisibleCount(24);
  }, [search, activeCategory]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8 md:space-y-10">
      {/* ── Hero ── */}
      <section className="relative overflow-hidden rounded-3xl border border-outline-variant/10 bg-surface-container-low px-6 py-10 md:px-12 md:py-14">
        <NetworkBackground />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="space-y-3 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-[0.15em]">
              <span className="material-symbols-outlined text-sm">hub</span>
              Exclusive Network
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-on-surface leading-tight">
              Build your{' '}
              <span className="text-primary">connection network</span>
            </h1>
            <p className="text-on-surface-variant text-base md:text-lg leading-relaxed">
              Reach out to {HIRING_CONTACTS.length}+ hand-picked recruiters, talent acquisition
              specialists, and hiring leaders. Every connection is a door opened.
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
            <button
              onClick={handleLucky}
              className="group flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="material-symbols-outlined text-lg transition-transform group-hover:rotate-12">
                auto_awesome
              </span>
              I&apos;m Feeling Lucky
            </button>
            <p className="text-[11px] text-on-surface-variant">
              Discover a random recruiter to connect with
            </p>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative z-10 mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: 'group', label: 'Total Contacts', value: HIRING_CONTACTS.length },
            { icon: 'category', label: 'Categories', value: categories.length - 1 },
            { icon: 'apartment', label: 'Companies', value: new Set(HIRING_CONTACTS.map((c) => c.company)).size },
            { icon: 'language', label: 'Regions', value: 'Global' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 rounded-xl bg-surface-container-high/60 backdrop-blur-sm border border-outline-variant/10 px-4 py-3"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-primary text-lg">{stat.icon}</span>
              </div>
              <div>
                <p className="text-lg font-extrabold text-on-surface leading-none">{stat.value}</p>
                <p className="text-[10px] text-on-surface-variant font-medium mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Lucky contact toast ── */}
      {luckyContact && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center gap-4 rounded-2xl bg-surface-container-lowest border border-outline-variant/20 shadow-2xl p-4 max-w-sm">
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getGradient(
                luckyContact.firstName + luckyContact.lastName,
              )} flex items-center justify-center text-white font-bold text-sm shrink-0`}
            >
              {getInitials(luckyContact.firstName, luckyContact.lastName)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-on-surface truncate">
                {luckyContact.firstName} {luckyContact.lastName}
              </p>
              <p className="text-xs text-on-surface-variant truncate">{luckyContact.position}</p>
            </div>
            <a
              href={luckyContact.url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 px-3 py-1.5 rounded-lg primary-gradient text-on-primary text-xs font-bold"
            >
              Connect
            </a>
          </div>
        </div>
      )}

      {/* ── Search & Filters ── */}
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-lg">
              search
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, company, or position..."
              className="input-field pl-10 pr-4 py-2.5 w-full rounded-xl text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant shrink-0">
            <span className="material-symbols-outlined text-base">filter_list</span>
            <span className="font-medium">{filtered.length} results</span>
          </div>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2">
          {categories.map(([label, count]) => (
            <CategoryChip
              key={label}
              label={label === 'all' ? 'All Contacts' : label}
              count={count}
              active={activeCategory === label}
              onClick={() => setActiveCategory(label)}
            />
          ))}
        </div>
      </section>

      {/* ── Contact Grid ── */}
      <section>
        {filtered.length === 0 ? (
          <EmptyState onClear={handleClear} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {visible.map((contact, i) => (
                <ContactCard
                  key={contact.url + i}
                  contact={contact}
                  index={i}
                  connected={connected.has(contact.url)}
                  onConnect={handleConnect}
                />
              ))}
            </div>

            {visible.length < filtered.length && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={handleLoadMore}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-surface-container-high text-on-surface text-sm font-bold hover:bg-surface-container transition-colors border border-outline-variant/10"
                >
                  <span className="material-symbols-outlined text-lg">expand_more</span>
                  Load more ({filtered.length - visible.length} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Networking Tip ── */}
      <section className="rounded-2xl border border-outline-variant/10 bg-gradient-to-r from-primary/5 to-secondary/5 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-primary text-2xl">lightbulb</span>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-on-surface mb-0.5">Pro networking tip</h4>
          <p className="text-sm text-on-surface-variant leading-relaxed">
            When reaching out, mention something specific about their company or a recent post.
            Personalized messages get 3x higher response rates than generic connection requests.
          </p>
        </div>
      </section>
    </div>
  );
}
